import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Save, Web, Warning } from '@mui/icons-material';
import api from '../../services/api';

const WebsiteSettingsContent = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    showAdminLogin: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/config/website-settings');
      if (response.data.success && response.data.websiteSettings) {
        setSettings({
          showAdminLogin: response.data.websiteSettings.showAdminLogin || false
        });
      }
    } catch (err) {
      console.error('Error fetching website settings:', err);
      setError('Failed to load website settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const response = await api.post('/config/website-settings', {
        websiteSettings: settings,
        updatedBy: 'admin',
      });
      
      if (response.data.success) {
        setSuccess('Website settings saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error saving website settings:', err);
      setError('Failed to save website settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          These settings control global website template behavior across all customer websites.
          Changes here will affect all website templates that fetch configuration from this portal.
        </Typography>
      </Alert>

      {/* Admin Panel Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Web sx={{ mr: 1, color: '#1976d2' }} />
            <Typography variant="h6">Admin Panel Settings</Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showAdminLogin}
                  onChange={(e) => setSettings({ ...settings, showAdminLogin: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Show Admin Login Link in Footer
                  </Typography>
                </Box>
              }
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mt: 0.5 }}>
              Display "Admin Login" link in website template footers. This allows website administrators 
              to access the admin panel directly from the public website.
            </Typography>
          </Box>

          {!settings.showAdminLogin && (
            <Alert severity="warning" icon={<Warning />} sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Admin Login is currently disabled.</strong> The "Admin Login" link will not appear 
                in website template footers. Enable this when the admin panel is fully functional and ready 
                for use.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Website Settings'}
        </Button>
      </Box>
    </Box>
  );
};

export default WebsiteSettingsContent;

